export function getStateLabelByCountry(countryCode) {
  const EU_COUNTRIES = [
    'AT', // Austria
    'BE', // Belgium
    'BG', // Bulgaria
    'HR', // Croatia
    'CY', // Cyprus
    'CZ', // Czech Republic
    'DK', // Denmark
    'EE', // Estonia
    'FI', // Finland
    'FR', // France
    'DE', // Germany
    'GR', // Greece
    'HU', // Hungary
    'IE', // Ireland
    'IT', // Italy
    'LV', // Latvia
    'LT', // Lithuania
    'LU', // Luxembourg
    'MT', // Malta
    'NL', // Netherlands
    'PL', // Poland
    'PT', // Portugal
    'RO', // Romania
    'SK', // Slovakia
    'SI', // Slovenia
    'ES', // Spain
    'SE'  // Sweden
  ];

  const mapping = {
    US: 'State',
    MX: 'State',
    CA: 'Province/Territory',
    GB: 'Country/Locality (Optional)',
    NO: 'Region (Optional)',
    CH: 'Canton (Optional)',
    JP: 'Prefecture'
  };

  const upperCode = countryCode?.toUpperCase();
  if (EU_COUNTRIES.includes(upperCode)) {
    return 'Region (Optional)';
  }

  return mapping[upperCode] || 'State/Region';
}

export function isStateRequired(countryCode) {
  const nonRequiredStateCountries = [
    'GB', 'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 
    'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE', 'CH', 'NO'
  ];
  const upperCode = countryCode?.toUpperCase();
  return !nonRequiredStateCountries.includes(upperCode);
}

// USPS Pub 28 Appendix C2 secondary-unit designators — the full set FedEx
// standardization emits. `#` marks an unknown designator (e.g. "# 286").
const SECONDARY_UNIT_DESIGNATORS = [
  'APT', 'BSMT', 'BLDG', 'DEPT', 'FL', 'FRNT', 'HNGR', 'KEY',
  'LBBY', 'LOT', 'LOWR', 'OFC', 'PH', 'PIER', 'REAR', 'RM',
  'SIDE', 'SLIP', 'SPC', 'STOP', 'STE', 'TRLR', 'UNIT', 'UPPR',
];
// Full-token match so street names that merely start with LOT/KEY/etc. don't
// false-positive. Some designators legitimately appear with no number (BSMT,
// FRNT, LBBY, LOWR, OFC, PH, REAR, SIDE, UPPR).
const UNIT_TOKEN_REGEX = new RegExp(
  `^(?:${SECONDARY_UNIT_DESIGNATORS.join('|')})(?:\\s+\\S+)?$|^#\\s*\\S+$`
);

// True when a STANDARDIZED token is a secondary-unit line ("APT 286",
// "# 286", "BSMT"). Not meant for raw user input, which uses long forms
// ("Suite 200", "Apartment 4").
export function isUnitToken(token) {
  return UNIT_TOKEN_REGEX.test(String(token || '').trim().toUpperCase());
}

/**
 * Map FedEx `street_lines_token` onto the form's two street fields.
 * The apt field must only ever hold an actual secondary-unit line: when the
 * LAST token is one, it becomes addressLine2 and the preceding tokens join
 * into the street; otherwise everything (company/CO lines included) stays in
 * the street and addressLine2 is empty.
 */
export function splitStreetLinesToken(tokens) {
  const lines = (Array.isArray(tokens) ? tokens : [])
    .map(line => String(line || '').trim())
    .filter(Boolean);
  if (lines.length > 1 && isUnitToken(lines[lines.length - 1])) {
    return {
      street: lines.slice(0, -1).join(', '),
      addressLine2: lines[lines.length - 1],
    };
  }
  return { street: lines.join(', '), addressLine2: '' };
}
